import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import { apiService } from '../../services/ApiService';
import { describeDtEvent } from '../../utils/dtEventFormat';

/**
 * NotificationBell — header inbox (DT Alpha, Epic C, spec §11).
 *
 * Self-contained: polls the unread badge on a slow interval (60s — deliberate,
 * see the API-polling antipattern in CLAUDE.md), loads the inbox on open,
 * marks read one-by-one or all at once. Clicking a service-call notification
 * navigates via onOpenServiceCalls.
 */

const POLL_INTERVAL_MS = 60_000;

// Semantic hues — fixed across themes (match the --dt-* semantic tokens).
const C_ACCENT = '#1A979B';
const C_BAD = '#e0574f';
const C_GOOD = '#2f9e6e';
const C_PROG = '#8f78ef';
const C_WARN = '#dd9a2b';

const svg = (children: React.ReactNode) => <svg viewBox="0 0 24 24">{children}</svg>;
const IC_TICKET = svg(<><path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0l-8-8V3h9.4l8.6 8.6a2 2 0 0 1 0 2.8z" /><circle cx="7.5" cy="7.5" r="1.1" /></>);
const IC_ALERT = svg(<><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>);
const IC_REFRESH = svg(<><path d="M23 4v6h-6" /><path d="M20.5 15a9 9 0 1 1-2.1-9.4L23 10" /></>);
const IC_COMMENT = svg(<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />);
const IC_CHECK = svg(<path d="M20 6L9 17l-5-5" />);
const IC_ACTIVITY = svg(<path d="M22 12h-4l-3 9L9 3l-3 9H2" />);

// Notification-inbox glyph/hue per event type (mockup screen-nt .ntic tiles).
const notifGlyph = (type: string): { icon: React.ReactNode; color: string } => {
  switch (type) {
    case 'call.created': return { icon: IC_TICKET, color: C_BAD };
    case 'iot.alert': return { icon: IC_ALERT, color: C_BAD };
    // Silence is a warning, not a breach — the reading is missing, not out of range.
    case 'iot.offline': return { icon: IC_ALERT, color: C_WARN };
    case 'call.updated': return { icon: IC_REFRESH, color: C_PROG };
    case 'call.commented': return { icon: IC_COMMENT, color: C_ACCENT };
    case 'call.resolved': return { icon: IC_CHECK, color: C_GOOD };
    // SLA notices (migration 063). Both use the alert glyph because both mean "a deadline has
    // passed"; the breach is the harder failure, so only it takes the bad hue.
    case 'call.sla_breach': return { icon: IC_ALERT, color: C_BAD };
    case 'call.sla_escalation': return { icon: IC_ALERT, color: C_WARN };
    default: return { icon: IC_ACTIVITY, color: C_ACCENT };
  }
};

// Compact relative time for the inbox meta line (e.g. "4 min ago"), in the APP's language —
// not the browser's, which left "6 days ago" in English under a Hebrew interface.
const relTime = (iso: string, locale: string): string => {
  const d = new Date(iso).getTime();
  if (isNaN(d)) return '';
  const s = Math.round((d - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const a = Math.abs(s);
  if (a < 60) return rtf.format(Math.round(s), 'second');
  if (a < 3600) return rtf.format(Math.round(s / 60), 'minute');
  if (a < 86400) return rtf.format(Math.round(s / 3600), 'hour');
  return rtf.format(Math.round(s / 86400), 'day');
};

interface NotificationRow {
  id: number;
  read_at: string | null;
  created_at: string;
  type: string;
  asset_id: string;
  ref_table: string | null;
  ref_id: string | null;
  payload: Record<string, any>;
  asset_name: string | null;
  actor_name: string | null;
}

interface NotificationBellProps {
  dismiss?: boolean;
  onOpen?: () => void;
  /** Navigate to the Service Calls page (payload carries callId for context). */
  onOpenServiceCalls?: (siteId: string, callId?: number) => void;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ onOpenServiceCalls, dismiss, onOpen }) => {
  const { t, currentLanguage } = useTranslation();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationRow[] | null>(null);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs] = useState<{ in_app: boolean; email: boolean; whatsapp: boolean; whatsapp_number: string } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const refreshCount = useCallback(async () => {
    try {
      const result = await apiService.get('/notifications/unread-count');
      setUnread(typeof result?.count === 'number' ? result.count : 0);
    } catch { /* badge poll is best-effort */ }
  }, []);

  useEffect(() => {
    void refreshCount();
    const timer = setInterval(() => void refreshCount(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [refreshCount]);

  useEffect(() => { if (dismiss) setOpen(false); }, [dismiss]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const openPanel = useCallback(async () => {
    onOpen?.();
    setOpen(prev => !prev);
    try {
      const list = await apiService.get('/notifications');
      setItems(Array.isArray(list) ? list : []);
    } catch {
      setItems([]);
    }
  }, [onOpen]);

  const markRead = useCallback(async (n: NotificationRow) => {
    if (!n.read_at) {
      setItems(prev => prev?.map(x => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x) || null);
      setUnread(prev => Math.max(0, prev - 1));
      apiService.post(`/notifications/${n.id}/read`, {}).catch(() => { /* re-synced on next poll */ });
    }
    if (n.type.startsWith('call.') && onOpenServiceCalls) {
      setOpen(false);
      const id = Number(n.payload?.callId || n.ref_id);
      onOpenServiceCalls(n.asset_id, Number.isInteger(id) && id > 0 ? id : undefined);
    }
  }, [onOpenServiceCalls]);

  const markAllRead = useCallback(async () => {
    setItems(prev => prev?.map(x => ({ ...x, read_at: x.read_at || new Date().toISOString() })) || null);
    setUnread(0);
    apiService.post('/notifications/read-all', {}).catch(() => { /* re-synced on next poll */ });
  }, []);

  const togglePrefs = useCallback(async () => {
    setPrefsOpen(prev => !prev);
    if (!prefs) {
      try {
        const p = await apiService.get('/notification-prefs');
        setPrefs({
          in_app: p.in_app !== false,
          email: p.email === true,
          whatsapp: p.whatsapp === true,
          whatsapp_number: p.whatsapp_number || '',
        });
      } catch {
        setPrefs({ in_app: true, email: false, whatsapp: false, whatsapp_number: '' });
      }
    }
  }, [prefs]);

  const savePrefs = useCallback((next: NonNullable<typeof prefs>) => {
    setPrefs(next);
    apiService.put('/notification-prefs', next).catch(() => { /* keep local state; retried on next change */ });
  }, []);

  return (
    <div ref={rootRef} className="dt-header-notifications dt-header-menu-anchor" onKeyDown={event => {
      if (event.key === 'Escape') { setOpen(false); rootRef.current?.querySelector('button')?.focus(); }
    }}>
      {/* The same 38px button as the language button next to it. */}
      <button
        type="button" className="dt-hbtn" aria-expanded={open} aria-label={t('dt_notif_title')}
        onClick={() => void openPanel()}
        title={t('dt_notif_title')}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" className="dt-header-ic"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>
        {unread > 0 && <span className="dt-hbtn-badge">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && (
        <div className="dt-pop dt-header-notification-popup">
          <div className="dt-notif-head">
            <b>{t('dt_notif_title')}</b>
            {unread > 0 && <span className="dt-pill2">{unread} {t('dt_notif_new')}</span>}
            <span className="a">
              {unread > 0 && (
                <button type="button" className="dt-notif-link" onClick={() => void markAllRead()}>
                  {t('dt_notif_markAll')}
                </button>
              )}
              <button type="button" className="dt-vwticon" onClick={() => void togglePrefs()}
                aria-label={t('dt_notif_prefs')} aria-expanded={prefsOpen} title={t('dt_notif_prefs')}>
                <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></svg>
              </button>
            </span>
          </div>

          {prefsOpen && prefs && (
            <div className="dt-notif-prefs">
              <label className="dt-cbrow">
                <input type="checkbox" checked={prefs.in_app}
                  onChange={e => savePrefs({ ...prefs, in_app: e.target.checked })} />
                {t('dt_notif_inApp')}
              </label>
              <label className="dt-cbrow">
                <input type="checkbox" checked={prefs.email}
                  onChange={e => savePrefs({ ...prefs, email: e.target.checked })} />
                {t('dt_notif_email')}
              </label>
            </div>
          )}
          {items === null ? (
            <div className="dt-header-menu-state">{t('dt_loading')}</div>
          ) : items.length === 0 ? (
            <div className="dt-notif-empty">
              <span className="dt-notif-empty-ic">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>
              </span>
              <span>{t('dt_notif_empty')}</span>
            </div>
          ) : (
            items.map(n => {
              const g = notifGlyph(n.type);
              return (
                <button
                  type="button"
                  key={n.id}
                  className={`dt-ntitem dt-notif-row${n.read_at ? ' read' : ''}`}
                  onClick={() => void markRead(n)}
                >
                  <span className="dt-ntic" style={{ background: `${g.color}22`, color: g.color }}>{g.icon}</span>
                  <span className="dt-notif-body">
                    <span className="dt-ntt">{describeDtEvent(n, t, { commentText: true, locale: currentLanguage })}</span>
                    {/* The SITE comes first. The inbox is per person, across every site they
                        can reach, while the rest of the header speaks for one site — so an
                        unlabelled "Device stopped reporting" read as this site's, even on a
                        site with no devices at all. */}
                    <span className="dt-ntm">
                      {[n.asset_name, n.actor_name, relTime(n.created_at, currentLanguage)].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  {!n.read_at && <span className="dt-ntdot" />}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
